import type { AddNoteRequest } from '@contracts/note/AddNoteRequest';
import { yupResolver } from '@hookform/resolvers/yup';
import CloseIcon from '@mui/icons-material/Close';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import { useAuthenticatedUser } from '@src/authentication/useAuthenticatedUser';
import { isClientUser } from '@src/clientUser/useClientUser';
import { Accordion } from '@src/common/components/Accordion';
import { Button } from '@src/common/components/button/Button';
import { Divider } from '@src/common/components/Divider';
import { PageLayout } from '@src/common/components/Page';
import { Stack } from '@src/common/components/Stack';
import { Typography } from '@src/common/components/Typography';
import { useToast } from '@src/common/hooks/useToast';
import { MeasurementToggle } from '@src/common/measurements/MeasurementToggle';
import { css } from '@src/common/styles/Styles';
import { theme } from '@src/common/styles/Theme';
import { parseInputValueAsNumber } from '@src/common/utils/formUtils';
import type { UploadFile } from '@src/documents/components/FormFileUpload';
import { getLastPartOfPath } from '@src/documents/hooks/useFindAllDocumentsForOrder';
import { DetailsItem } from '@src/orders/components/DetailsItem';
import { LastUpdatedBanner } from '@src/orders/components/LastUpdatedBanner';
import type {
  Order,
  SubmittedPermitApplication,
} from '@src/orders/domain/Order';
import { getStatePermitUploadFile } from '@src/orders/domain/Order';
import { useNavigateToOrderDetails } from '@src/orders/hooks/useNavigateToOrderDetails';
import { useRefreshOrders } from '@src/orders/hooks/useRefreshOrders';
import { CompletePermitActions } from '@src/permits/completePermit/CompletePermitActions';
import { CompletePermitWithNoteModal } from '@src/permits/completePermit/CompletePermitWithNoteModal';
import { ConfirmDeliveryModal } from '@src/permits/completePermit/ConfirmDeliveryModal';
import type {
  CompletePermitSchemaType,
  ErrorPermitFields,
} from '@src/permits/completePermit/fields/CompletePermitFields';
import {
  CompleteAction,
  CompletePermitSchema,
  CompletionTypeField,
  EffectiveDateField,
  ExpirationDateField,
  isNoteRequired,
  JurisdictionFeeField,
  ServiceFeeField,
  StatePermitDocumentFields,
  StatePermitNumberField,
} from '@src/permits/completePermit/fields/CompletePermitFields';
import { PermitDetails } from '@src/permits/completePermit/PermitDetails';
import { PermitInformation } from '@src/permits/completePermit/PermitInformation';
import { useCompleteApplication } from '@src/permits/hooks/useCompleteApplication';
import { usePermitMeasurement } from '@src/permits/hooks/usePermitMeasurement';
import { useSaveApplicationStatement } from '@src/permits/hooks/useSaveApplicationStatement';
import { PermitFees } from '@src/permits/permitFees/PermitFees';
import { useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import type * as yup from 'yup';

type Props = {
  permitApplication: SubmittedPermitApplication;
  order: Order;
};

export const CompletePermit = ({ permitApplication, order }: Props) => {
  const { t } = useTranslation();
  const { navigateToOrderDetails } = useNavigateToOrderDetails();
  const { refreshOrders } = useRefreshOrders();
  const { showSuccessToast, showErrorToast } = useToast();
  const { showInMetric, setShowInMetric } = usePermitMeasurement();
  const { user } = useAuthenticatedUser();
  const location = useLocation();
  const allowUpdate = location.state?.allowUpdate;

  const readOnly =
    allowUpdate !== undefined
      ? !allowUpdate
      : permitApplication.application.isReadOnly();
  const isSelfIssued = permitApplication.permitTypeIsSelfIssued;
  const validateExpirationDate = permitApplication.validateExpirationDate;
  const validateExpirationTime = permitApplication.validateExpirationTime;
  const initialStatePermit = getStatePermitUploadFile(permitApplication);
  const {
    register,
    formState,
    watch,
    getValues,
    handleSubmit,
    control,
    setValue,
  } = useForm({
    resolver: yupResolver(CompletePermitSchema),
    context: {
      isSelfIssued,
      validateExpirationDate,
      validateExpirationTime,
    },
    defaultValues: {
      [EffectiveDateField.fieldName]:
        permitApplication.application.effectiveDate.getDate(),
      [ExpirationDateField.fieldName]:
        permitApplication.application.expirationDate?.getDate(),
      [StatePermitNumberField.fieldName]:
        permitApplication.application.statePermitNumber,
      [JurisdictionFeeField.fieldName]: permitApplication.fees?.jurisdictionFee,
      [ServiceFeeField.fieldName]: permitApplication.fees?.serviceFee,
      [StatePermitDocumentFields.fieldName]: initialStatePermit,
      documents: permitApplication.application.otherDocuments.map((doc) => ({
        documentId: doc.documentId,
        name: getLastPartOfPath(doc.key),
        sendToClient: doc.sendToClient,
        showToClient: doc.showToClient,
      })),
      conditionsSheets: permitApplication.application.conditionsSheets.map(
        (conditionsSheet) => ({
          id: conditionsSheet.id,
          filePath: conditionsSheet.filePath,
          sendToClient: conditionsSheet.sendToClient,
          showToClient: conditionsSheet.showToClient,
        }),
      ),
    },
    mode: 'onChange',
  });

  const [confirmDeliveryOpen, setConfirmDeliveryOpen] = useState(false);
  const openConfirmDelivery = () => setConfirmDeliveryOpen(true);
  const closeConfirmDelivery = () => setConfirmDeliveryOpen(false);

  const [validatedPermitValues, setValidatedPermitValues] = useState<
    ErrorPermitFields | undefined
  >(undefined);
  const selectedAction = getValues(CompletionTypeField.fieldName)
    ? CompleteAction[getValues(CompletionTypeField.fieldName)]
    : '';

  const requireNote = isNoteRequired(selectedAction);

  const openAddNoteModal = (data: CompletePermitSchemaType) => {
    setValidatedPermitValues(data as ErrorPermitFields);
  };
  const closeAddNoteModal = () => {
    setValidatedPermitValues(undefined);
  };

  const redirectToOrderDetail = () => {
    refreshOrders();
    void navigateToOrderDetails({ orderId: order.id });
  };

  const { completeApplication } = useCompleteApplication({
    permitApplicationId: permitApplication.application.id,
    orderId: order.id,
    userId: user.id,
  });

  const { saveApplicationStatement } = useSaveApplicationStatement({
    permitApplicationId: permitApplication.application.id,
    onSuccess: () => {
      showSuccessToast(t('changesSaved'));
      redirectToOrderDetail();
    },
    onError: () => {
      showErrorToast(t('saveChangesError'));
    },
  });

  const onSubmit = (
    data: Omit<
      yup.InferType<typeof CompletePermitSchema>,
      'statePermitDocument'
    >,
  ) => {
    if (!requireNote) {
      completeApplication({ completePermitForm: data });
    } else {
      openAddNoteModal(data);
    }
  };

  const completePermitWithNote = async (
    data: ErrorPermitFields,
    note: AddNoteRequest,
  ) => {
    completeApplication({ completePermitForm: data, note });
  };

  const saveProgress = () => {
    const data = {
      [JurisdictionFeeField.fieldName]: parseInputValueAsNumber(
        getValues(JurisdictionFeeField.fieldName),
      ),
      [ServiceFeeField.fieldName]: parseInputValueAsNumber(
        getValues(ServiceFeeField.fieldName),
      ),
      [EffectiveDateField.fieldName]: getValues(EffectiveDateField.fieldName),
      [ExpirationDateField.fieldName]:
        getValues(ExpirationDateField.fieldName) || undefined,
      [StatePermitNumberField.fieldName]:
        getValues(StatePermitNumberField.fieldName) || undefined,
    };
    saveApplicationStatement(data);
  };

  const isAddNoteOpen = validatedPermitValues !== undefined;

  const statePermit = useWatch({
    control,
    name: 'statePermitDocument',
  }) as UploadFile | undefined;
  const uploadedDocuments = (
    useWatch({
      control,
      name: 'documents',
    }) ?? []
  ).map((doc: UploadFile & { check?: boolean }) => ({
    ...doc,
    sendToClient: doc.check ?? doc.sendToClient,
  }));
  const conditionSheets =
    useWatch({
      control,
      name: 'conditionsSheets',
    }) ?? [];
  const showBanner =
    order.changeRequests?.getLatestChangeRequest() &&
    order.changeRequests!.getLatestChangeRequest()!.requestedAt >
      permitApplication.application.createdAt.getDate();
  return (
    <PageLayout
      title={
        readOnly ? t('permitApplication.view') : t('permitApplication.complete')
      }
    >
      {showBanner && <LastUpdatedBanner />}
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h6" className={orderId}>
          {`${t('order')} ${order.displayId}`}
        </Typography>
        <MeasurementToggle
          isMetric={showInMetric}
          setIsMetric={setShowInMetric}
        />
      </Stack>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack gap="12px">
          <ApplicationSummary
            order={order}
            permitApplication={permitApplication}
          />
          <Accordion id="permit-information" title={t('permitInformation')}>
            <PermitInformation
              permitApplication={permitApplication.application}
              order={order}
              isMetric={showInMetric}
              permitConfirmationNumber={
                isSelfIssued
                  ? '-'
                  : permitApplication.submission.confirmationNumber
              }
            />
          </Accordion>
          <Divider />
          <Accordion
            id="permit-details"
            title={t('permitDetails')}
            defaultExpanded
          >
            <PermitDetails
              permitApplication={permitApplication.application}
              orderId={order.id}
              control={control}
              setValue={setValue}
              getValues={getValues}
              readOnly={readOnly}
              isSelfIssued={isSelfIssued}
              validateExpirationDate={validateExpirationDate}
              validateExpirationTime={validateExpirationTime}
            />
          </Accordion>
          {!isClientUser(user) && (
            <>
              <Divider />
              <Accordion
                id="permit-fees"
                title={t('permitFees')}
                defaultExpanded
              >
                <PermitFees
                  register={register}
                  formState={formState}
                  watch={watch}
                  currency={permitApplication.permitTypeCurrency}
                  readOnly={readOnly}
                />
              </Accordion>
            </>
          )}
          <Divider style={{ marginBottom: '24px' }} />
          {!readOnly && (
            <Stack direction="row" justifyContent="space-between">
              <Stack direction="row" gap={theme.spacing[2]}>
                <Button
                  type="button"
                  className={button}
                  onClick={redirectToOrderDetail}
                >
                  <CloseIcon /> {t('abort')}
                </Button>

                <Button
                  type="button"
                  className={button}
                  variant="outlined"
                  onClick={saveProgress}
                >
                  <SaveOutlinedIcon /> {t('saveProgress')}
                </Button>
              </Stack>

              <CompletePermitActions
                onClickConfirmDelivery={openConfirmDelivery}
                key="complete-permit-actions"
                control={control}
                getValues={getValues}
              />
            </Stack>
          )}
          {readOnly && (
            <Stack direction="row" justifyContent="space-between">
              <Stack direction="row" gap={theme.spacing[2]}>
                <Button
                  type="button"
                  className={button}
                  onClick={redirectToOrderDetail}
                >
                  <CloseIcon /> {t('close')}
                </Button>
              </Stack>
            </Stack>
          )}
        </Stack>
      </form>
      {isAddNoteOpen && requireNote && (
        <CompletePermitWithNoteModal
          open={isAddNoteOpen}
          onClose={closeAddNoteModal}
          onConfirm={async (note: AddNoteRequest) => {
            await completePermitWithNote(validatedPermitValues, note);
            closeAddNoteModal();
          }}
          application={permitApplication}
          permitNumber={
            validatedPermitValues?.statePermitNumber ??
            permitApplication.application.statePermitNumber
          }
          context={
            selectedAction as CompleteAction.DENY | CompleteAction.FLAG_ERROR
          }
        />
      )}

      {selectedAction === CompleteAction.DELIVER && (
        <ConfirmDeliveryModal
          statePermitDocument={statePermit}
          documents={uploadedDocuments}
          conditionSheets={conditionSheets}
          open={confirmDeliveryOpen}
          onClose={closeConfirmDelivery}
          onConfirm={handleSubmit(onSubmit)}
        />
      )}
    </PageLayout>
  );
};

const ApplicationSummary = ({ order, permitApplication }: Props) => {
  const { t } = useTranslation();

  const isSelfIssued = permitApplication.permitTypeIsSelfIssued;
  return (
    <Stack gap="6px">
      <Typography variant="body2" className={permitType}>
        {t('permit.name', {
          permitTypeName: permitApplication.permitTypeName,
          permitTypeJurisdiction: permitApplication.permitTypeJurisdiction,
        })}
      </Typography>

      <Divider />

      <DetailsItem
        itemName={t('permit.for')}
        value={order.clientName}
        labelVariant="subtitle2"
      />
      <DetailsItem
        itemName={t('fields.doingBusinessAs')}
        value={order.doingBusinessAs}
        labelVariant="subtitle2"
      />
      <DetailsItem
        labelVariant="subtitle2"
        itemName={t('assignTo')}
        value={permitApplication.application.assignee?.name}
      />
      <DetailsItem
        labelVariant="subtitle2"
        itemName={t('submitted')}
        value={
          isSelfIssued
            ? undefined
            : permitApplication.submission.submissionDate?.formatUTCDate()
        }
      />
    </Stack>
  );
};

const orderId = css((theme) => ({
  paddingBottom: theme.spacing['5'],
  fontWeight: 400,
}));

const permitType = css(() => ({
  fontWeight: 600,
}));

const button = css({
  padding: '0 18px',
  height: '40px',
  gap: '6px',
});
